import React from 'react';

const DEBOUNCE_MS = 200;
const HEIGHT_PADDING = 1;

export function useIframeAutoHeight() {
	const iframeRef = React.useRef<HTMLIFrameElement>(null);
	const [height, setHeight] = React.useState('0px');

	React.useEffect(() => {
		const iframe = iframeRef.current;

		if (!iframe) {
			return;
		}

		const syncHeight = () => {
			const contentHeight = iframe.contentWindow?.document.body.scrollHeight ?? 0;
			const newHeight = `${contentHeight + HEIGHT_PADDING}px`;

			// DOM을 직접 업데이트해 React 렌더 사이클 지연 없이 즉시 반영
			iframe.style.height = newHeight;
			setHeight(newHeight);
		};

		let resizeObserver: ResizeObserver | null = null;

		const observeIframeBody = () => {
			const body = iframe.contentDocument?.body;

			if (!body) {
				return;
			}

			resizeObserver?.disconnect();
			resizeObserver = new ResizeObserver(syncHeight);
			resizeObserver.observe(body);
		};

		const handleIframeLoad = () => {
			syncHeight();
			observeIframeBody();
		};

		iframe.addEventListener('load', handleIframeLoad);

		if (iframe.contentDocument?.readyState === 'complete') {
			syncHeight();
			observeIframeBody();
		}

		let debounceTimer: ReturnType<typeof setTimeout>;
		const handleResize = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(syncHeight, DEBOUNCE_MS);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			iframe.removeEventListener('load', handleIframeLoad);
			window.removeEventListener('resize', handleResize);

			clearTimeout(debounceTimer);
			resizeObserver?.disconnect();
		};
	}, []);

	return { iframeRef, height };
}
